import {Box, Tooltip, Typography} from '@mui/material';
import {AppIconButton} from 'components/buttons';
import {AppSprintSelect, AppStatusSelect} from 'components/form/Select';
import FeautureGuard from 'guards/FeautureGuard';
import {Add, CloseSquare} from 'iconsax-react';
import {ItemDataResponse} from 'models/board';
import {TenantSettingsKeysEnum} from 'models/settings';
import React, {useState} from 'react';
import {Control} from 'react-hook-form';
import {useLocation} from 'react-router-dom';
import {TimeTrackerPopover} from 'views/TimeTracker';
import TimeTrackerAddUpdateManulEntryDialog from 'views/TimeTracker/TimeTrackerAddUpdateManulEntryDialog';

export interface ItemDrawerHeaderProps {
    item: ItemDataResponse;
    loading?: boolean;
    ticketNo?: number;
    handleDrawerClose: () => void;
    control: Control<any, any>;
    boardSlug: string;
}

export const ItemDrawerHeader: React.FC<ItemDrawerHeaderProps> = ({
    handleDrawerClose,
    control,
    item,
    ticketNo,
    loading,
    boardSlug,
}) => {
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const handleOpenCreateDialog = () => {
        setOpenCreateDialog(true);
    };
    const handleCloseCreateDialog = () => {
        setOpenCreateDialog(false);
    };
    const location = useLocation();
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
            <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, width: '100%', mb: 2}}>
                {/* Ticket number and status section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', sm: 'row'},
                        flex: 1,
                        gap: 2,
                        alignItems: {xs: 'flex-start', sm: 'center'},
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: {xs: '100%', sm: '150px'},
                        }}
                    >
                        <Typography variant="h4">{`Ticket No: #${ticketNo}`}</Typography>
                        {!location.pathname.includes('my-work') && (
                            <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                                <Tooltip title="Close">
                                    <AppIconButton isLoading={loading} color="error" onClick={handleDrawerClose}>
                                        <CloseSquare size={40} variant="Outline" />
                                    </AppIconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{width: {xs: '100%', sm: '200px'}}}>
                        <AppStatusSelect
                            fullWidth
                            disableClearable
                            label=""
                            shouldReturnValueOnly
                            boardSlug={boardSlug}
                            control={control}
                            name="itemStatusId"
                        />
                    </Box>
                </Box>

                {/* Right side section with time tracker and sprint */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', sm: 'row'},
                        alignItems: 'center',
                        gap: 2,
                        mt: {xs: 2, sm: 0},
                        justifyContent: 'flex-end',
                        width: {xs: '100%', sm: '400px'},
                    }}
                >
                    <FeautureGuard featuresKeys={[TenantSettingsKeysEnum.TenantEnableTimeTracker.toString()]}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #bfbfbf',
                                borderRadius: '10px',
                                backgroundColor: 'background.paper',
                                boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
                                width: {xs: '100%', sm: '200px'},
                            }}
                        >
                            <TimeTrackerPopover showPopper={false} itemDataId={item.id} item={item} />
                            <AppIconButton
                                isLoading={loading}
                                tooltip="Add manual entry"
                                variant="none"
                                onClick={handleOpenCreateDialog}
                            >
                                <Add />
                            </AppIconButton>
                            <TimeTrackerAddUpdateManulEntryDialog
                                afterUpdateAndCreateHandler={() => {}}
                                open={openCreateDialog}
                                itemDataId={item.id}
                                onCloseDialog={handleCloseCreateDialog}
                            />
                        </Box>
                    </FeautureGuard>

                    <FeautureGuard featuresKeys={[TenantSettingsKeysEnum.TenantEnableSprints.toString()]}>
                        <Box sx={{width: {xs: '100%', sm: '150px'}}}>
                            <AppSprintSelect
                                fullWidth
                                control={control}
                                boardSlug={boardSlug}
                                label=""
                                shouldReturnValueOnly
                                name="sprintId"
                            />
                        </Box>
                    </FeautureGuard>

                    {!location.pathname.includes('my-work') && (
                        <Box sx={{display: {xs: 'none', sm: 'flex'}}}>
                            <Tooltip title="Close">
                                <AppIconButton isLoading={loading} color="error" onClick={handleDrawerClose}>
                                    <CloseSquare size={40} variant="Outline" />
                                </AppIconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ItemDrawerHeader;
